import * as EmailValidator from 'email-validator'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {
    contactUsPageEmailLinkActioned,
    ContactUsPageEmailLinkActionedAction,
} from '@/analytics/segment'
import FeatureBlock from '@/components/feature-block'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import PrismicLink from '@/components/prismic-link'
import {Text} from '@/components/text'
import {Layout, Section} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {tokens, radii, colors} from '@/theme'

interface Props {
    title?: string
    body?: string
    items: Item[]
}

interface Item {
    title: string
    destination: string
    destinationLink?: PrismicLinkProps
}

export const ContactEmails = ({title, body, items}: Props) => {
    const handleEmailLinkActioned = (email: string) => {
        contactUsPageEmailLinkActioned({
            action: ContactUsPageEmailLinkActionedAction.Completed,
            email,
        })
    }

    return (
        <Section>
            <FeatureBlock title={title} body={body} headingLevel="h2" />

            <StyledLayout as="ul" $variant="four-equal">
                {items.map(({title: emailTitle, destination, destinationLink}) => {
                    const isEmail = EmailValidator.validate(destination)

                    return (
                        <li key={destination}>
                            <CardButtonLink
                                as={isEmail ? 'a' : PrismicLink}
                                href={isEmail ? `mailto:${destination}` : undefined}
                                aria-label={isEmail ? `Send an email to ${emailTitle}` : undefined}
                                onClick={() => handleEmailLinkActioned(destination)}
                                {...destinationLink}
                            >
                                <Text>{emailTitle}</Text>
                                <SelectableText $variant="paragraph-base" $color="lightMuted">
                                    {destination}
                                </SelectableText>
                            </CardButtonLink>
                        </li>
                    )
                })}
            </StyledLayout>
        </Section>
    )
}

const StyledLayout = styled(Layout)`
    padding-left: 0;
    margin-top: ${tokens.spacing40};
    margin-bottom: 0;
    list-style-type: none;

    li {
        height: 100%;
    }
`

const CardButtonLink = styled.div`
    display: grid;
    grid-template-rows: auto 1fr;
    gap: ${tokens.spacing10};
    height: 100%;
    padding: ${tokens.spacing20};
    background-color: ${colors.background.muted};
    border: ${({theme}) => theme.borders.transparent};
    border-radius: ${radii.arc8};

    &:hover {
        border-color: ${tokens.shade900};
    }

    &:focus {
        ${focusStyles()}
    }
`

const SelectableText = styled(Text)`
    -webkit-user-select: all;
    user-select: all;
`

export const contactEmailsSliceFragment = graphql`
    fragment ContactEmailsSlice on PrismicSlicePageDataBodyContactEmails {
        __typename
        primary {
            title
            body1
        }
        items {
            contact_title
            contact_email
            destination_link {
                ...PrismicLink
            }
        }
    }
`
